import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import {
  Button,
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import axios from "axios";
import { AuthContext } from "../Context/AuthContext";
import API_BASE_URL from "../../utils/api";
import QuillEditor from "../QuillEditor/QuillEditor";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const CommentBox = ({ threadId, threadAuthorId }) => {
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "info",
  });
  const [expandedComments, setExpandedComments] = useState({});
  const toggleComment = (id) =>
    setExpandedComments((prev) => ({ ...prev, [id]: !prev[id] }));

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };
  const quillRef = useRef(null);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/comments/${threadId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setComments(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to load comments",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchComments();
  }, [threadId]);

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(
        `${API_BASE_URL}/comments`,
        { threadId, comment: newComment },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setNewComment("");
      fetchComments();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to post comment",
        type: "error",
      });
    }
  };

  const handleEditComment = async (commentId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/comments/${commentId}`,
        { comment: editContent },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setEditingCommentId(null);
      setEditContent("");
      fetchComments();
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update comment",
        type: "error",
      });
    }
  };

  const handleDeleteComment = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/comments/${deleteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchComments();
      setDeleteId(null);
      setDialogOpen(false);
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to delete comment",
        type: "error",
      });
    }
  };

  const canModify = (commentUserId) => {
    if (!user) return false;
    return (
      user.role === "admin" ||
      threadAuthorId === user._id ||
      commentUserId === user._id
    );
  };

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "align",
    "link",
    "code-block",
  ];
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ["16px"] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  return (
    <Box sx={{ mb: 2 }}>
      {/* Comment List */}

      <List sx={{ mb: 2 }}>
        {comments.map((c) => (
          <Paper
            key={c._id}
            elevation={0}
            sx={{
              p: 1.8,
              mb: 1.6,
              borderRadius: 3,
              border: "1px solid #eee",
              backgroundColor: "#ffffff",
              position: "relative",
              overflow: "hidden",
              "&:before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                backgroundColor: "#FFD700",
                opacity: 0.6,
              },
              transition: "0.2s",
              "&:hover": {
                boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                transform: "translateY(-1px)",
              },
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}
            >
              <Avatar
                src={c.user?.profileImage}
                alt={c.user?.name}
                sx={{ width: 38, height: 38 }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }} noWrap>
                  {c.user?.name || "Anonymous"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatDistanceToNow(new Date(c.createdAt), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>

              {canModify(c.user?._id) && (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingCommentId(c._id);
                      setEditContent(c.comment);
                    }}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeleteId(c._id);
                      setDialogOpen(true);
                    }}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {editingCommentId === c._id ? (
              <Box sx={{ width: "100%", mb: 2 }}>
                <QuillEditor
                  ref={quillRef}
                  value={editContent}
                  onChange={setEditContent}
                  modules={modules}
                  formats={formats}
                  placeholder="Edit your comment..."
                />
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => handleEditComment(c._id)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setEditingCommentId(null)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              (() => {
                const plain = stripHtml(c.comment);
                const isLong = plain.length > 180;
                const isExpanded = !!expandedComments[c._id];

                return (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#2b2b2b",
                        lineHeight: 1.7,
                        display: isExpanded ? "block" : "-webkit-box",
                        WebkitLineClamp: isExpanded ? "unset" : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {plain}
                    </Typography>

                    {isLong && (
                      <Button
                        size="small"
                        onClick={() => toggleComment(c._id)}
                        sx={{
                          mt: 0.6,
                          textTransform: "none",
                          fontWeight: 700,
                          px: 0,
                          minWidth: "unset",
                        }}
                      >
                        {isExpanded ? "Show less" : "Read more"}
                      </Button>
                    )}
                  </>
                );
              })()
            )}
          </Paper>
        ))}
      </List>

      {/* Divider for new comment */}
      {user && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid #eee",
            backgroundColor: "#fafafa",
          }}
        >
          {" "}
          <Typography sx={{ mb: 1, fontWeight: 800 }}>
            Write a Comment
          </Typography>
          <QuillEditor
            ref={quillRef}
            value={newComment}
            onChange={setNewComment}
            modules={modules}
            formats={formats}
            placeholder="Share your thoughts..."
          />
          <Button
            sx={{
              mt: 1.2,
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              backgroundColor: "#FFD700",
              color: "#111",
              "&:hover": { backgroundColor: "#FFB800" },
            }}
            onClick={handlePostComment}
            variant="contained"
          >
            Post Comment
          </Button>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this comment? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteComment}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for API errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommentBox;
